import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import LanguageSwitcher from '../LanguageSwitcher';
import ThemeToggle from '../ThemeToggle';

const PortalHeader: React.FC = () => {
  const { t } = useTranslation();
  const { client, logout } = useClientAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/portal/dashboard', label: t('nav.dashboard') },
    { path: '/portal/usage', label: t('nav.usage') },
    { path: '/portal/correction', label: t('nav.correction') },
    { path: '/portal/webhooks', label: t('nav.webhooks') },
    { path: '/portal/alerts', label: t('nav.alerts') },
    { path: '/portal/help', label: t('nav.help') },
    { path: '/portal/settings', label: t('nav.settings') },
  ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center gap-3 h-16">
          {/* Logo/Title + Navigation */}
          <div className="flex items-center space-x-4 min-w-0">
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-blue-600">Solar Forecast API</h1>
              <p className="text-xs text-gray-500">{t('common.clientPortal')}</p>
            </div>

            {/* Navigation Links (desktop) */}
            <nav className="hidden md:flex space-x-1 min-w-0 overflow-x-auto">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Language + theme + User Menu + mobile hamburger */}
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 hover:bg-gray-50 px-2 sm:px-3 py-2 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium text-sm">
                    {client?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-gray-700">{client?.name}</p>
                  <p className="text-xs text-gray-500">{client?.email}</p>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform hidden sm:block ${
                    showUserMenu ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-700 truncate">{client?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{client?.email}</p>
                      </div>
                      <button
                        onClick={logout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        {t('common.logout')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Hamburger (mobile only — shown when the nav bar doesn't fit) */}
            <button
              type="button"
              onClick={() => setShowMobileMenu((v) => !v)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Menu"
              aria-expanded={showMobileMenu}
            >
              {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile navigation panel */}
      {showMobileMenu && (
        <nav className="md:hidden border-t border-gray-200 bg-white px-4 py-2 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setShowMobileMenu(false)}
                className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      )}
    </header>
  );
};

export default PortalHeader;
